import { useTranslation } from '@/context/AuthContext';
import { useState, useEffect, useCallback } from 'react';
import { getIncidents, createIncident, updateIncident, replanIncident, createCompensationClaim } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertTriangle, Plus, Loader2, CheckCircle, ArrowRight, Shield, Plane, CloudRain, Thermometer, Clock } from 'lucide-react';
import { toast } from 'sonner';

export const IncidentMode = ({ tripId, trip }) => {
  const { t } = useTranslation();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: 'delay', title: '', description: '', severity: 'medium' });
  const [replanData, setReplanData] = useState({});
  const [replanning, setReplanning] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await getIncidents(tripId);
      setIncidents(res.data);
    } catch {}
    setLoading(false);
  }, [tripId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title) return;
    await createIncident(tripId, form);
    setForm({ type: 'delay', title: '', description: '', severity: 'medium' });
    setOpen(false);
    toast.success('Incident reported');
    load();
  };

  const handleReplan = async (incidentId) => {
    setReplanning(prev => ({ ...prev, [incidentId]: true }));
    try {
      const res = await replanIncident(incidentId);
      setReplanData(prev => ({ ...prev, [incidentId]: res.data }));
      toast.success('Replanning complete');
      load();
    } catch (e) {
      toast.error('Replanning failed');
    }
    setReplanning(prev => ({ ...prev, [incidentId]: false }));
  };

  const handleResolve = async (incidentId) => {
    await updateIncident(incidentId, { status: 'resolved' });
    toast.success('Incident resolved');
    load();
  };

  const typeIcons = {
    delay: Clock, cancellation: Plane, illness: Thermometer,
    weather: CloudRain, missed_transfer: AlertTriangle
  };
  const typeLabels = { delay: 'Retard', cancellation: 'Annulation', illness: 'Maladie', weather: 'Meteo', missed_transfer: 'Correspondance ratee' };
  const severityColors = {
    low: 'bg-blue-50 text-blue-700 border-blue-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    high: 'bg-orange-50 text-orange-700 border-orange-200',
    critical: 'bg-red-50 text-red-700 border-red-200'
  };
  const statusColors = {
    open: 'bg-red-50 text-red-700', replanning: 'bg-amber-50 text-amber-700',
    resolved: 'bg-emerald-50 text-emerald-700', closed: 'bg-gray-100 text-gray-600'
  };

  if (loading) return <div className="h-32 bg-white rounded-2xl animate-pulse" />;

  return (
    <div className="space-y-4" data-testid="incident-mode">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-red-500 hover:bg-red-600 text-white" data-testid="report-incident-btn">
              <AlertTriangle className="w-4 h-4 mr-1.5" /> Signaler un incident
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Signaler un incident</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <div>
                <Label className="text-sm">Type</Label>
                <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                  <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(typeLabels).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Titre</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 h-11" placeholder="Vol AE123 retarde de 3h" data-testid="incident-title" />
              </div>
              <div>
                <Label className="text-sm">Description</Label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1" rows={3} data-testid="incident-desc" />
              </div>
              <div>
                <Label className="text-sm">Severite</Label>
                <Select value={form.severity} onValueChange={v => setForm({...form, severity: v})}>
                  <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Faible</SelectItem>
                    <SelectItem value="medium">Moyen</SelectItem>
                    <SelectItem value="high">Eleve</SelectItem>
                    <SelectItem value="critical">Critique</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleCreate} className="w-full rounded-xl bg-red-500 hover:bg-red-600 text-white" data-testid="save-incident-btn">Signaler</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {incidents.length === 0 ? (
        <div className="widget-card p-10 text-center">
          <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-3" strokeWidth={1.2} />
          <p className="text-sm text-[#64748B]">Aucun incident signale. Tout va bien !</p>
        </div>
      ) : (
        <div className="space-y-4">
          {incidents.map(inc => {
            const Icon = typeIcons[inc.type] || AlertTriangle;
            const rp = replanData[inc.incident_id];
            return (
              <div key={inc.incident_id} className="widget-card overflow-hidden" data-testid={`incident-${inc.incident_id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-red-500" strokeWidth={1.8} />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1E293B]">{inc.title}</p>
                        <p className="text-xs text-[#94A3B8]">{typeLabels[inc.type] || inc.type}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Badge className={`text-[10px] rounded-lg border-0 ${severityColors[inc.severity]}`}>{inc.severity}</Badge>
                      <Badge className={`text-[10px] rounded-lg border-0 ${statusColors[inc.status]}`}>{inc.status}</Badge>
                    </div>
                  </div>
                  {inc.description && <p className="text-sm text-[#64748B] mb-3">{inc.description}</p>}

                  <div className="flex gap-2">
                    {inc.status === 'open' && (
                      <Button
                        onClick={() => handleReplan(inc.incident_id)}
                        disabled={replanning[inc.incident_id]}
                        className="rounded-xl bg-[#3A86FF] hover:bg-[#2563EB] text-white text-xs h-9"
                        data-testid={`replan-${inc.incident_id}`}
                      >
                        {replanning[inc.incident_id] ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Replanning...</> : <><ArrowRight className="w-3.5 h-3.5 mr-1" /> Replanifier cette journee</>}
                      </Button>
                    )}
                    {inc.status !== 'resolved' && inc.status !== 'closed' && (
                      <Button variant="outline" onClick={() => handleResolve(inc.incident_id)} className="rounded-xl text-xs h-9 border-emerald-200 text-emerald-600 hover:bg-emerald-50">
                        <CheckCircle className="w-3.5 h-3.5 mr-1" /> Resoudre
                      </Button>
                    )}
                  </div>
                </div>

                {/* Replan results */}
                {rp && (
                  <div className="border-t border-[#F1F5F9] p-5 bg-[#FAFBFE] space-y-4">
                    <h4 className="text-sm font-bold text-[#1E293B]">Plans alternatifs</h4>
                    <div className="grid md:grid-cols-3 gap-3">
                      {(rp.plans || []).map((plan, i) => (
                        <div key={i} className="bg-white rounded-xl border border-[#E2E8F0] p-4">
                          <div className="flex items-center justify-between mb-2">
                            <Badge className="bg-[#3A86FF]/10 text-[#3A86FF] border-0 text-xs font-bold">Plan {plan.plan}</Badge>
                            <Badge className={`text-[9px] border-0 rounded-md ${plan.risk_level === 'low' ? 'bg-emerald-50 text-emerald-600' : plan.risk_level === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
                              Risque {plan.risk_level}
                            </Badge>
                          </div>
                          <p className="text-sm font-semibold text-[#1E293B] mb-1">{plan.title}</p>
                          <p className="text-xs text-[#64748B] mb-2">{plan.description}</p>
                          {plan.steps && (
                            <div className="space-y-1">
                              {plan.steps.slice(0, 3).map((s, j) => (
                                <p key={j} className="text-[10px] text-[#94A3B8] flex items-start gap-1">
                                  <span className="text-[#3A86FF] font-bold">{j+1}.</span> {s}
                                </p>
                              ))}
                            </div>
                          )}
                          {plan.estimated_cost && <p className="text-xs font-medium text-[#3A86FF] mt-2">{plan.estimated_cost}</p>}
                        </div>
                      ))}
                    </div>
                    {rp.crisis_checklist?.length > 0 && (
                      <div>
                        <h5 className="text-xs font-bold text-[#1E293B] mb-2">Checklist de crise</h5>
                        <div className="flex flex-wrap gap-2">
                          {rp.crisis_checklist.map((c, i) => (
                            <Badge key={i} variant="outline" className="text-[10px] rounded-lg border-[#E2E8F0]">{c}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
